import {isNativeCapacitorPlatform} from './platform';

export const GOOGLE_ANALYTICS_MEASUREMENT_ID = 'G-GLKQJ96WJ7';

type GoogleAnalyticsWindow = typeof globalThis & {
  dataLayer?: unknown[][];
  gtag?: (...args: unknown[]) => void;
  __invemoryGoogleAnalyticsConfigured?: boolean;
  [key: `ga-disable-${string}`]: boolean | undefined;
};

const ADVERTISING_DENIED = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
} as const;
const LANGUAGE_SEGMENTS = new Set(['en', 'de', 'uk', 'ru']);
const PRIVATE_ROUTE_PARENTS = new Set(['containers', 'items', 'profiles', 'publication-requests', 'users']);
const STATIC_ROUTE_VALUES = new Set(['create', 'new']);

export function recordFavoriteWebEvent(
  name: 'favorites_widget_view' | 'favorite_added' | 'favorite_removed' | 'favorite_opened' | 'favorites_widget_hidden',
  params: Record<string, string>,
): void {
  if (isNativeCapacitorPlatform() || typeof window === 'undefined') return;
  const analyticsWindow = getGoogleAnalyticsWindow(globalThis);
  if (!analyticsWindow.__invemoryGoogleAnalyticsConfigured || analyticsWindow[`ga-disable-${GOOGLE_ANALYTICS_MEASUREMENT_ID}`] !== false) return;
  analyticsWindow.gtag?.('event', name, params);
}
const PRIVATE_VALUE_PATTERN = /^(?:\d+|[0-9a-f]{8}-[0-9a-f-]{27,}|[a-z0-9_-]{20,})$/i;

function getDocument(): Document | null {
  return typeof document === 'undefined' ? null : document;
}

function getGoogleAnalyticsWindow(globalScope: typeof globalThis): GoogleAnalyticsWindow {
  return globalScope as GoogleAnalyticsWindow;
}

function ensureGtag(globalWindow: GoogleAnalyticsWindow): (...args: unknown[]) => void {
  globalWindow.dataLayer ??= [];
  globalWindow.gtag ??= (...args: unknown[]) => {
    globalWindow.dataLayer?.push(args);
  };
  return globalWindow.gtag;
}

export function sanitizeGoogleAnalyticsPagePath(url: string): string {
  const path = url.split(/[?#]/, 1)[0] ?? '/';
  const segments = path.split('/').filter(Boolean);
  const languageOffset = LANGUAGE_SEGMENTS.has(segments[0]?.toLowerCase()) ? 1 : 0;

  const safeSegments = segments.map((segment, index) => {
    const normalizedSegment = segment.toLowerCase();
    const parentSegment = segments[index - 1]?.toLowerCase();
    const routeIndex = index - languageOffset;
    const isPublicItemIdentifier = segments[languageOffset]?.toLowerCase() === 'items'
      && (routeIndex === 1 || routeIndex === 2);
    const isPrivateRouteSlot = PRIVATE_ROUTE_PARENTS.has(parentSegment)
      && !STATIC_ROUTE_VALUES.has(normalizedSegment);

    return PRIVATE_VALUE_PATTERN.test(segment) || isPrivateRouteSlot || isPublicItemIdentifier
      ? 'id'
      : normalizedSegment;
  });

  return safeSegments.length > 0 ? `/${safeSegments.join('/')}` : '/';
}

export function initializeGoogleAnalytics(
  globalScope: typeof globalThis = globalThis,
  documentScope: Document | null = getDocument(),
): boolean {
  if (isNativeCapacitorPlatform() || !documentScope) {
    return false;
  }

  const globalWindow = getGoogleAnalyticsWindow(globalScope);
  globalWindow[`ga-disable-${GOOGLE_ANALYTICS_MEASUREMENT_ID}`] = false;
  const gtag = ensureGtag(globalWindow);

  gtag('consent', 'default', {
    analytics_storage: 'granted',
    ...ADVERTISING_DENIED,
  });

  if (!globalWindow.__invemoryGoogleAnalyticsConfigured) {
    const safePagePath = sanitizeGoogleAnalyticsPagePath(globalScope.location?.href ?? '/');
    const safePageLocation = globalScope.location?.origin
      ? `${globalScope.location.origin}${safePagePath}`
      : safePagePath;
    gtag('js', new Date());
    gtag('config', GOOGLE_ANALYTICS_MEASUREMENT_ID, {
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      page_location: safePageLocation,
      page_path: safePagePath,
      page_referrer: '',
      page_title: 'Invemory',
      send_page_view: false,
    });
    gtag('event', 'page_view', {
      page_location: safePageLocation,
      page_path: safePagePath,
      page_referrer: '',
      page_title: 'Invemory',
    });
    globalWindow.__invemoryGoogleAnalyticsConfigured = true;
  } else {
    gtag('consent', 'update', {
      analytics_storage: 'granted',
      ...ADVERTISING_DENIED,
    });
  }

  if (!documentScope.querySelector('script[data-google-analytics="true"]')) {
    const script = documentScope.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS_MEASUREMENT_ID}`;
    script.setAttribute('data-google-analytics', 'true');
    documentScope.head.appendChild(script);
  }

  return true;
}

export function disableGoogleAnalytics(
  globalScope: typeof globalThis = globalThis,
  documentScope: Document | null = getDocument(),
): void {
  const globalWindow = getGoogleAnalyticsWindow(globalScope);
  globalWindow[`ga-disable-${GOOGLE_ANALYTICS_MEASUREMENT_ID}`] = true;
  globalWindow.gtag?.('consent', 'update', {
    analytics_storage: 'denied',
    ...ADVERTISING_DENIED,
  });

  if (documentScope) {
    deleteGoogleAnalyticsCookies(documentScope);
  }
}

export function updateGoogleAnalyticsConsent(granted: boolean): boolean {
  if (granted) {
    return initializeGoogleAnalytics();
  }

  disableGoogleAnalytics();
  return false;
}

export function buildGoogleAnalyticsCookieDeletionDirectives(
  cookieName: string,
  hostname: string,
  pathname: string,
): string[] {
  const normalizedHostname = hostname.replace(/^\.+|\.+$/g, '');
  const hostnameParts = normalizedHostname.split('.').filter(Boolean);
  const domains = new Set<string>(['']);
  if (hostnameParts.length > 1 && normalizedHostname !== 'localhost') {
    for (let index = 0; index <= hostnameParts.length - 2; index += 1) {
      const domain = hostnameParts.slice(index).join('.');
      domains.add(domain);
      domains.add(`.${domain}`);
    }
  }

  const paths = new Set<string>(['/']);
  const pathSegments = pathname.split('/').filter(Boolean);
  let currentPath = '';
  for (const segment of pathSegments) {
    currentPath += `/${segment}`;
    paths.add(currentPath);
    paths.add(`${currentPath}/`);
  }

  const encodedName = encodeURIComponent(cookieName);
  const directives: string[] = [];
  for (const domain of domains) {
    for (const path of paths) {
      directives.push(
        `${encodedName}=; Path=${path};${domain ? ` Domain=${domain};` : ''} Expires=Thu, 01 Jan 1970 00:00:00 GMT; Max-Age=0; SameSite=Lax`,
      );
    }
  }
  return directives;
}

export function deleteGoogleAnalyticsCookies(documentScope: Document = document): void {
  const cookieNames = documentScope.cookie
    .split(';')
    .map((cookie) => decodeURIComponent(cookie.split('=', 1)[0]?.trim() ?? ''))
    .filter((name) => name === '_ga' || name.startsWith('_ga_'));

  for (const cookieName of new Set(cookieNames)) {
    for (const directive of buildGoogleAnalyticsCookieDeletionDirectives(
      cookieName,
      documentScope.location.hostname,
      documentScope.location.pathname,
    )) {
      documentScope.cookie = directive;
    }
  }
}
